// src/hooks/useUnsavedCaseGuard.js
//
// Warns before a half-filled, not-yet-created case is thrown away:
//   - closing / refreshing the tab → the browser's own "Leave site?" prompt
//     (browsers do not allow custom text there);
//   - any in-app navigation (sidebar, back button, Cancel, links) → our
//     "Stay on case / Leave anyway" prompt via useBlocker;
//   - following a notification → NotificationContext asks getUnsavedCase()
//     first and offers to open the link in a new tab instead.
//
// One module-level slot is enough: only one case form is ever mounted.

import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { Modal } from 'antd';

let unsavedCase = null;

/** The label of the case form currently holding unsaved data, or null. */
export const getUnsavedCase = () => unsavedCase;

/**
 * True once anything has been typed into the gold item table. The rate is
 * prefilled from the live feed, so it does not count as user input.
 */
export const hasGoldItemInput = (goldItems = []) =>
  goldItems.length > 1 || goldItems.some((g) => Boolean(
    (g.itemName && g.itemName.trim())
    || g.purity > 0
    || g.grossWeight > 0
    || (g.stoneWaxDeduction ?? g.deduction) > 0
    || g.dustDeduction > 0,
  ));

/**
 * Returns allowLeave(): call it right before navigating away on a successful
 * Create, so the form's own redirect is not blocked.
 */
export function useUnsavedCaseGuard(active, label) {
  const leavingRef = useRef(false);
  const allowLeave = useCallback(() => { leavingRef.current = true; }, []);

  useEffect(() => {
    if (!active) return undefined;
    unsavedCase = label;

    const onBeforeUnload = (e) => {
      if (leavingRef.current) return;
      e.preventDefault();
      // Older Chrome/Edge only show the prompt when returnValue is set.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (unsavedCase === label) unsavedCase = null;
    };
  }, [active, label]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => (
    active && !leavingRef.current && currentLocation.pathname !== nextLocation.pathname
  ));

  // The blocker object is recreated every render; key the prompt off its state
  // so one blocked navigation opens exactly one dialog.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    Modal.confirm({
      title: `This ${label} has not been created yet`,
      content: 'If you leave this page now, everything entered in the case will be lost.',
      okText: 'Leave anyway',
      okButtonProps: { danger: true },
      cancelText: 'Stay on case',
      onOk: () => blockerRef.current.proceed?.(),
      onCancel: () => blockerRef.current.reset?.(),
    });
  }, [blocker.state, label]);

  return allowLeave;
}
